import ajax from '@utils/ajax';
import { SocialIdInvalidModal } from 'SsnValidation.mod';
import { currentEditingEmployeeId, validateWithBE } from 'dynamic-form';
import { RATE_LIMIT_ERROR } from './common';

/**
 * Cache to store validation results for each employee's social ID.
 * This prevents showing the modal multiple times for the same value
 * after the user has already made a decision.
 */
const overriddenValueSocialId: Record<
	string,
	{ value: string; valid: boolean; employeeId: string }
> = {};

/**
 * Tracks ongoing validation promises to prevent duplicate validation requests.
 * This prevents multiple simultaneous validations of the same value,
 * which could result in multiple modals being shown.
 */
const pendingValidations: Record<string, Promise<boolean | string>> = {};

/**
 * Validates Social Security Numbers (NIN), Social Insurance Numbers (SIN),
 * and National IDs to prevent duplicates in the system.
 *
 * The validation flow:
 * 1. Checks if the value is empty or unchanged (returns true)
 * 2. Checks the cache for previous validation results
 * 3. Checks for pending validations of the same value
 * 4. Makes a backend request to validate the value
 * 5. If duplicate found, shows a confirmation modal to the user
 * 6. Caches the result for future validations
 *
 * @param context - The dynamic form context
 * @param field - The field data containing settings and initial value
 * @param params - Validation parameters including:
 *                - action: The validation action to perform
 *                - check_field: The field name to validate
 *                - friendly_name: Display name for the field
 *                - message: Error message to show if validation fails
 * @returns Promise resolving to:
 *          - true if validation passes
 *          - error message if validation fails
 *          - RATE_LIMIT_ERROR if too many requests
 */
export const validateSocialId = (
	context: DynamicForm.Context,
	field: DynamicForm.Data,
	params: any,
) => {
	const { action, check_field, friendly_name, message } = params;
	const field_name = (friendly_name || check_field).toUpperCase();

	return async (value): Promise<boolean | string> => {
		if (!value || value === field?.settings?.initialValue) {
			// don't make a BE request - field isn't filled in yet, let required or regex run
			// don't make a BE request if it matches the initial value
			return true;
		}

		const employeeId = currentEditingEmployeeId();
		const cacheKey = `${String(employeeId)}_${check_field}`;
		const validationKey = `${cacheKey}_${value}`;

		// Check cache first
		if (overriddenValueSocialId[cacheKey]?.value === value) {
			return overriddenValueSocialId[cacheKey].valid;
		}

		// Check if there's already a pending validation for this value
		if (pendingValidations[validationKey]) {
			return pendingValidations[validationKey];
		}

		let validationPromise: Promise<boolean | string>;

		try {
			validationPromise = (async () => {
				const data = {
					id: employeeId,
					check_field,
					action,
					[check_field]: value,
				};

				const { isValid, isRateLimited } = await validateWithBE(
					ajax.post,
					data,
				);
				if (isRateLimited) {
					delete pendingValidations[validationKey];
					return RATE_LIMIT_ERROR;
				}

				let finalIsValid = isValid;
				if (!isValid) {
					let resolve;
					const modalPromise = new Promise<boolean>((res) => {
						resolve = res;
					});

					const msg = $.__(
						'There is already an employee with this %1 in the system. Are you sure you want to create another one?',
						field_name,
					);
					const title = $.__('Duplicate %1', field_name);
					SocialIdInvalidModal(title, msg, resolve);
					finalIsValid = await modalPromise;
				}

				// Cache the result
				overriddenValueSocialId[cacheKey] = {
					value,
					valid: finalIsValid,
					employeeId: String(employeeId),
				};

				return finalIsValid || message;
			})();

			// Store the promise immediately after creation
			pendingValidations[validationKey] = validationPromise;

			// Wait for completion and then clean up
			validationPromise.finally(() => {
				delete pendingValidations[validationKey];
			});

			return validationPromise;
		} catch (error) {
			delete pendingValidations[validationKey];
			throw error;
		}
	};
};
